import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient, { setTokens } from "@/lib/api-client";
import type { Merchant } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  merchant_id: string;
  store_name?: string | null;
}

// Google OAuth redirect target.
//
// Google redirects here with ?code=<authorization_code>&state=<signed_jwt>.
// The PKCE code_verifier was generated server-side at GET /auth/google-login/url
// and stashed in sessionStorage by AuthContext.loginWithGoogle before the
// redirect to Google. We complete the flow by POSTing all three values to
// /auth/google-login, receive PREP JWTs, and navigate to the dashboard.
//
// SECURITY — tokens are never accepted from URL query parameters. Putting
// JWTs in the URL would leak them to browser history, HTTP Referer headers,
// reverse-proxy access logs, and any front-end analytics.
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const oauthError = searchParams.get("error");

    if (oauthError) {
      setError(
        oauthError === "access_denied"
          ? "You declined the Google sign-in request."
          : "Google sign-in failed. Please try again."
      );
      return;
    }

    if (!code || !state) {
      setError("Missing authorization code from Google. Please try again.");
      return;
    }

    const codeVerifier = sessionStorage.getItem("prep_google_code_verifier");
    const storedState = sessionStorage.getItem("prep_google_state");

    // Wipe the ephemeral PKCE material as soon as we have read it. Keeping
    // it around after the exchange adds no value and enlarges the window
    // an XSS attacker could exfiltrate it.
    sessionStorage.removeItem("prep_google_code_verifier");
    sessionStorage.removeItem("prep_google_state");

    if (!codeVerifier) {
      setError(
        "Sign-in session expired. Please start the Google sign-in flow again."
      );
      return;
    }

    // CSRF guard: the state returned by Google must match the one we stashed.
    // The backend also verifies the state's JWT signature; this client-side
    // check catches stale tabs / replayed callbacks early.
    if (storedState && storedState !== state) {
      setError("State mismatch detected. Please try signing in again.");
      return;
    }

    apiClient
      .post<TokenResponse>("/auth/google-login", {
        code,
        state,
        code_verifier: codeVerifier,
      })
      .then(({ data }) => {
        setTokens(data.access_token, data.refresh_token);
        return apiClient.get<Merchant>("/merchants/me");
      })
      .then(() => navigate("/app/dashboard", { replace: true }))
      .catch(() => {
        setError("Could not complete Google sign-in. Please try logging in again.");
      });
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-4">
          <p className="text-destructive font-medium">{error}</p>
          <a href="/login" className="text-primary hover:underline text-sm">
            Back to login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-muted-foreground">Completing sign-in…</p>
      </div>
    </div>
  );
}
